import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  DateRange as DateIcon,
  AttachMoney as SalaryIcon,
  Assignment as ContractedIcon,
  Description as CandidaturasIcon
} from '@mui/icons-material';
import { 
  buscarCandidatosContratados, 
  buscarEstatisticasGerais,
  CandidatoAprovadoContratado 
} from '../../services/prefeitura';
import { normalizarNome, normalizarEmail, normalizarTelefone } from '../../utils/normalization';


const GestaoPrefeitura: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [candidatosContratados, setCandidatosContratados] = useState<CandidatoAprovadoContratado[]>([]);
  const [estatisticas, setEstatisticas] = useState({
    totalContratados: 0,
    totalCandidaturas: 0,
    empresasParticipantes: 0,
    candidatosProcessados: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados para paginação e busca
  const [searchTerm, setSearchTerm] = useState('');
  const [pageContratados, setPageContratados] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [contratados, stats] = await Promise.all([
        buscarCandidatosContratados(),
        buscarEstatisticasGerais()
      ]);
      
      setCandidatosContratados(contratados);
      setEstatisticas(stats);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados da gestão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };


  const filtrarCandidatos = (candidatos: CandidatoAprovadoContratado[]) => {
    return candidatos.filter(candidato => {
      const termoBusca = searchTerm.toLowerCase();
      return (
        (candidato.candidatoNome?.toLowerCase() || '').includes(termoBusca) ||
        (candidato.empresaNome?.toLowerCase() || '').includes(termoBusca) ||
        (candidato.vagaNome?.toLowerCase() || '').includes(termoBusca) ||
        (candidato.cargo?.toLowerCase() || '').includes(termoBusca)
      );
    });
  };

  const candidatosContratadosFiltrados = filtrarCandidatos(candidatosContratados);

  const formatarData = (data: string | undefined) => {
    if (!data) return 'Data não informada';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarSalario = (salario?: number) => {
    if (!salario) return 'Não informado';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(salario);
  };

  const renderCardsMobile = (candidatos: CandidatoAprovadoContratado[]) => {
    const candidatosPaginados = candidatos.slice(pageContratados * rowsPerPage, pageContratados * rowsPerPage + rowsPerPage);

    return (
      <Box>
        {candidatosPaginados.map((candidato) => (
          <Card key={candidato.id} sx={{ mb: 2 }} elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <ContractedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {normalizarNome(candidato.candidatoNome)}
                  </Typography>
                  <Chip 
                    label={candidato.status} 
                    size="small" 
                    color='success'
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon fontSize="small" sx={{ opacity: 0.7 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {normalizarNome(candidato.empresaNome)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkIcon fontSize="small" sx={{ opacity: 0.7 }} />
                  <Typography variant="body2">
                    {candidato.vagaNome || 'Vaga não informada'} - {candidato.cargo || 'Cargo não informado'}
                  </Typography>
                </Box>
                
                {candidato.salario && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SalaryIcon fontSize="small" sx={{ opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                      {formatarSalario(candidato.salario)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  const renderTabelaDesktop = (candidatos: CandidatoAprovadoContratado[]) => {
    const candidatosPaginados = candidatos.slice(pageContratados * rowsPerPage, pageContratados * rowsPerPage + rowsPerPage);

    return (
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Candidato</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Empresa</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vaga/Cargo</TableCell>
              {!isTablet && <TableCell sx={{ fontWeight: 600 }}>Salário</TableCell>}
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidatosPaginados.map((candidato) => (
              <TableRow key={candidato.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {normalizarNome(candidato.candidatoNome)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {normalizarNome(candidato.empresaNome)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {candidato.vagaNome || 'Vaga não informada'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {candidato.cargo || 'Cargo não informado'}
                    </Typography>
                  </Box>
                </TableCell>
                
                {!isTablet && (
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                      {formatarSalario(candidato.salario)}
                    </Typography>
                  </TableCell>
                )}
                
                <TableCell>
                  <Chip 
                    label={candidato.status} 
                    size="small" 
                    color='success'
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Cabeçalho */}
      <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: 1 }}>
        Gestão da Prefeitura
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Controle e acompanhamento de candidatos aprovados e contratados pelas empresas
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Cards de Estatísticas */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 2, 
        mb: 4,
        '& > *': { 
          flex: isMobile ? '1 1 calc(50% - 8px)' : '1 1 calc(25% - 12px)',
          minWidth: isMobile ? 'calc(50% - 8px)' : '200px'
        }
      }}>
        <Card elevation={2}>
          <CardContent sx={{ textAlign: 'center', py: isMobile ? 2 : 3 }}>
            <PersonIcon sx={{ fontSize: isMobile ? 30 : 40, color: 'info.main', mb: 1 }} />
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
              {estatisticas.candidatosProcessados}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Candidatos
            </Typography>
          </CardContent>
        </Card>
        
        <Card elevation={2}>
          <CardContent sx={{ textAlign: 'center', py: isMobile ? 2 : 3 }}>
            <CandidaturasIcon sx={{ fontSize: isMobile ? 30 : 40, color: 'primary.main', mb: 1 }} />
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
              {estatisticas.totalCandidaturas}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Candidaturas
            </Typography>
          </CardContent>
        </Card>
        
        <Card elevation={2}>
          <CardContent sx={{ textAlign: 'center', py: isMobile ? 2 : 3 }}>
            <ContractedIcon sx={{ fontSize: isMobile ? 30 : 40, color: 'success.main', mb: 1 }} />
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
              {estatisticas.totalContratados}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contratados
            </Typography>
          </CardContent>
        </Card>
        
        <Card elevation={2}>
          <CardContent sx={{ textAlign: 'center', py: isMobile ? 2 : 3 }}>
            <BusinessIcon sx={{ fontSize: isMobile ? 30 : 40, color: 'warning.main', mb: 1 }} />
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
              {estatisticas.empresasParticipantes}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Empresas Participantes
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Campo de Busca */}
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={isMobile ? "Buscar..." : "Buscar por candidato, empresa, vaga ou cargo..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {searchTerm && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
            Contratados encontrados: {candidatosContratadosFiltrados.length}
          </Typography>
        )}
      </Paper>

      {/* Lista de Candidatos Contratados */}
      <Paper elevation={3}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContractedIcon sx={{ color: 'success.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Candidatos Contratados ({candidatosContratadosFiltrados.length})
            </Typography>
          </Box>
        </Box>

        {candidatosContratadosFiltrados.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ContractedIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {searchTerm ? 'Nenhum candidato contratado encontrado para a busca' : 'Nenhum candidato contratado ainda'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {isMobile 
              ? renderCardsMobile(candidatosContratadosFiltrados)
              : renderTabelaDesktop(candidatosContratadosFiltrados)
            }
            
            <TablePagination
              component={Paper}
              elevation={1}
              sx={{ mt: 2 }}
              rowsPerPageOptions={[5, 10, 25]}
              count={candidatosContratadosFiltrados.length}
              rowsPerPage={rowsPerPage}
              page={pageContratados}
              onPageChange={(e, newPage) => setPageContratados(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPageContratados(0);
              }}
              labelRowsPerPage={isMobile ? "Por página:" : "Linhas por página:"}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default GestaoPrefeitura;